import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import { connect } from 'react-redux';
import { Cookie } from '@cotyorg/ccx-organism-cookie';
import { HeaderTwoRow } from '@cotyorg/ccx-organism-header';
import { FooterTwoRow } from '@cotyorg/ccx-organism-footer';
import { Social } from '@cotyorg/ccx-molecules/Social';
import Search from '@cotyorg/ccx-organism-search/components/Search';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import Paragraph from '@cotyorg/ccx-atoms/Paragraph';
import MikMakScript from '@cotyorg/ccx-molecules/MikMakScript';
import { trackPageView } from '@cotyorg/ccx-utils/js/tracking/google-analytics/events/page';
import readJSONFromScriptTag from '@cotyorg/ccx-utils/js/helpers/read-json-from-script-tag';
import { useMikMakAdobeTracking } from '@cotyorg/ccx-utils/js/hooks/useMikMakAdobeTracking';
import { useMikMakGaTracking } from '@cotyorg/ccx-utils/js/hooks/useMikMakGaTracking';
import { createWebPageSchema } from '../../utils/helpers/schema';

const buildWebPageSchemaForState = (state) => {
  const content = R.prop('content', state);
  const currentUrl = R.path(['status', 'currentUrl'], state);

  if (!currentUrl) {
    return null;
  }

  const metaTitle = R.path(['metaInformation', 'metaTitle'], content);
  const name = metaTitle ? metaTitle.split('|')[0].trim() : '';
  const description = R.path(['metaInformation', 'metaDescription'], content);

  return createWebPageSchema({
    url: currentUrl,
    name: name || undefined,
    description: description || undefined,
  });
};

const StandardLayout = ({
  contactDetails,
  cookieContent,
  children,
  isServedFromLegacySite,
  socialHeading,
  search,
  presentationLocale,
  isOneTrustEnabled,
  skipButtonText,
  mikMakConfig,
  webPageSchema,
}) => {
  useMikMakAdobeTracking();
  useMikMakGaTracking();
  // Page view tracking
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const serverTrackingData =
      readJSONFromScriptTag({ id: 'serverTrackingData' }) || {};

    const clientEnv = readJSONFromScriptTag({ id: 'clientEnv' }) || {};

    // Get previous location from sessionStorage
    const prevPageLocation = sessionStorage.getItem('prevPageLocation') || '';

    // Save current location as previous for next page
    sessionStorage.setItem('prevPageLocation', window.location.href);

    trackPageView({
      window,
      serverTrackingData: {
        ...serverTrackingData,
      },
      environment: clientEnv.ENVIRONMENT || 'staging',
      prevPageLocation,
      userConsent: clientEnv.USER_CONSENT || '',
    });
  }, []);
  return (
    <>
      <a href="#content" className="skip-link">
        {skipButtonText || 'Skip to content'}
      </a>
      <HeaderTwoRow
        logoSrc="/ccx-files/assets/logo.svg"
        logoAlt="Manhattan Cosmetics home"
        leftChevronIconSrc="/ccx-files/assets/arrow-left.svg"
        rightChevronIconSrc="/ccx-files/assets/arrow-right.svg"
        closeIconSrc="/ccx-files/assets/cross-white.svg"
        hamburgerIconSrc="/ccx-files/assets/hamburger.svg"
        logoPosition="center"
        themeName="filled"
        navType="featuredProducts"
        showSearch
        searchIconSrc="/ccx-files/assets/search.svg"
        logoWidth={305}
        logoHeight={75}
      />
      <Search
        appElement="#app"
        searchIconSrc="/ccx-files/assets/search-simple.svg"
        closeIconSrc="/ccx-files/assets/cancel-simple.svg"
        clearSearchIconSrc="/ccx-files/assets/erase-simple.svg"
        searchURL={R.prop('url')(search)}
        locale={R.prop('locale')(search)}
        presentationLocale={presentationLocale}
        searchPath="/search/"
        searchFieldName="search"
        searchButtonHidden
        isServedFromLegacySite={isServedFromLegacySite}
      />

      <div className="page__main-content" id="content">
        {children}
      </div>

      {!!webPageSchema && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(webPageSchema),
            }}
          />
        </>
      )}

      <div className="social__background">
        <div className="grid-container">
          <div className="grid-x grid-margin-x">
            <div className="cell medium-8">
              <div className="social__wrapper">
                <Social
                  socialAlignment="left"
                  socialIconSrc={{
                    facebook: '/ccx-files/assets/facebook-white.svg',
                    instagram: '/ccx-files/assets/instagram-white.svg',
                    pinterest: '/ccx-files/assets/pinterest-white.svg',
                    tiktok: '/ccx-files/assets/tiktok-white.svg',
                    twitter: '/ccx-files/assets/twitter-white.svg',
                    youtube: '/ccx-files/assets/youtube-white.svg',
                  }}
                  heading={socialHeading}
                />
              </div>
            </div>
            <div className="cell medium-8">
              <Paragraph extraClass="social-contact-heading" enableBBCode>
                {R.prop('description', contactDetails)}
              </Paragraph>
            </div>
          </div>
        </div>
      </div>

      <FooterTwoRow
        isServedFromLegacySite={isServedFromLegacySite}
        minusIconSrc="/ccx-files/assets/arrow-left.svg"
        plusIconSrc="/ccx-files/assets/arrow-left.svg"
        countrySelector={{
          visible: true,
          beforeCopyright: true,
          showToggleIcon: false,
        }}
        navigationLinkIconWidth={8}
        navigationLinkIconHeight={14}
      />
      <OptionalSection
        renderCondition={!isOneTrustEnabled}
        section={() => (
          <Cookie
            position="bottom"
            content={cookieContent}
            closeIconSrc="/ccx-files/assets/cross-white.svg"
          />
        )}
      />
      {mikMakConfig.enabled && <MikMakScript appId={mikMakConfig.appId} />}
    </>
  );
};

StandardLayout.propTypes = {
  contactDetails: PropTypes.shape({
    description: PropTypes.string,
  }),
  cookieContent: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    dismissText: PropTypes.string,
  }).isRequired,
  children: PropTypes.node,
  isServedFromLegacySite: PropTypes.bool,
  socialHeading: PropTypes.string,
  search: PropTypes.shape({
    url: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    contentLabel: PropTypes.string,
    placeholderText: PropTypes.string,
    clearSearchTermHistoryButtonText: PropTypes.string,
    popularSearches: PropTypes.array,
  }),
  skipButtonText: PropTypes.string,
  presentationLocale: PropTypes.string.isRequired,
  isOneTrustEnabled: PropTypes.bool,
  mikMakConfig: PropTypes.shape({
    appId: PropTypes.string,
    wtbId: PropTypes.string,
    enabled: PropTypes.bool,
  }),
  webPageSchema: PropTypes.object,
};

const mapStateToProps = (state) => ({
  cookieContent: R.path(['content', 'cookie'], state),
  contactDetails: R.path(['content', 'contactDetails'], state),
  isServedFromLegacySite: R.path(['status', 'isServedFromLegacySite'], state),
  search: R.path(['content', 'search'], state),
  presentationLocale: R.prop('presentationLocale', state),
  isOneTrustEnabled: R.path(['content', 'isOneTrustEnabled'], state),
  skipButtonText: R.path(['content', 'header', 'skipButtonText'], state),
  mikMakConfig: R.path(['content', 'mikmak'], state),
  webPageSchema: buildWebPageSchemaForState(state),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { omitWebPageSchema, ...restOwn } = ownProps;

  return {
    ...restOwn,
    ...stateProps,
    ...dispatchProps,
    webPageSchema: omitWebPageSchema ? null : stateProps.webPageSchema,
  };
};

export default connect(mapStateToProps, null, mergeProps)(StandardLayout);
